import type { ProposalNodeSet, RfqHubFileRelease, TimberSupplierStructureRef } from '../types';
import { normalizeQuotedStructures } from './timberQuoteStreamKey';
import { normalizeName } from './timberRfqStructureScope';

/**
 * Structure refs stamped on a staged supplier release (snapshot of the hub
 * track's structure tags at staging time). Empty = whole project / untagged.
 */
export function stagedReleaseStructureRefs(
  release: Pick<RfqHubFileRelease, 'structureIds' | 'structureNames'> | null | undefined,
): TimberSupplierStructureRef[] {
  if (!release?.structureIds?.length) return [];
  return normalizeQuotedStructures(
    release.structureIds.map((id, i) => ({ id, name: release.structureNames?.[i] || '' })),
  );
}

type StagedStructureSource = Pick<RfqHubFileRelease, 'structureIds' | 'structureNames'>;

function sumBoardFeet(timberList: ProposalNodeSet['materials']['timberList']): number {
  return Math.round(timberList.reduce((sum, item) => sum + (item.boardFeet || 0) * (item.quantity || 0), 0) * 100) / 100;
}

function mergeNodeSets(nodeSets: ProposalNodeSet[], ref: TimberSupplierStructureRef): ProposalNodeSet {
  const [first] = nodeSets;
  const timberList = nodeSets.flatMap((ns) => ns.materials?.timberList ?? []);
  const totalBoardFeet = sumBoardFeet(timberList);
  return {
    ...first,
    id: ref.id,
    name: ref.name,
    enabled: true,
    materials: { ...first.materials, timberList, totalBoardFeet },
    production: { ...first.production, boardFeet: totalBoardFeet },
  };
}

/**
 * Make a staged CSV's node sets line up with the hub structure(s) the release was
 * tagged to, so scope pickers, package filtering, and the request wizard all see the
 * same structure ids/names the Project Hub uses.
 *
 * - Untagged release (whole project): node sets pass through unchanged.
 * - Every tagged structure matches a Cadwork group by id or name: matching groups
 *   adopt the hub id + name; extra groups are kept as-is.
 * - Otherwise the file *is* the tagged structure(s) — the Cadwork grouping column
 *   ("Building 1", "Building 2") is just how the list was drawn — so every row is
 *   collapsed into one node set named after the tagged structure(s).
 */
export function alignNodeSetsToStagedStructures(
  nodeSets: ProposalNodeSet[],
  release: StagedStructureSource | null | undefined,
): ProposalNodeSet[] {
  const staged = stagedReleaseStructureRefs(release);
  if (!staged.length || !nodeSets.length) return nodeSets;

  const refForNodeSet = (ns: ProposalNodeSet): TimberSupplierStructureRef | undefined =>
    staged.find((s) => s.id === ns.id) ?? staged.find((s) => normalizeName(s.name) === normalizeName(ns.name));
  const matchedIds = new Set(nodeSets.map(refForNodeSet).filter(Boolean).map((s) => s!.id));
  const everyStagedMatched = staged.every((s) => matchedIds.has(s.id));

  if (everyStagedMatched) {
    return nodeSets.map((ns) => {
      const ref = refForNodeSet(ns);
      return ref ? { ...ns, id: ref.id, name: ref.name } : ns;
    });
  }

  const ref: TimberSupplierStructureRef =
    staged.length === 1
      ? staged[0]
      : { id: staged[0].id, name: staged.map((s) => s.name).join(' + ') };
  return [mergeNodeSets(nodeSets, ref)];
}

export function initialPublishStructureDraft(input: {
  targets: string[];
  options: TimberSupplierStructureRef[];
  supplierStructuresByOrg?: Record<string, TimberSupplierStructureRef[] | null>;
  /**
   * Structures the staged file was tagged to in the Project Hub. Used as the
   * default pick when the supplier's invite has no scope — the file only
   * covers those structures, so the quote request should too.
   */
  stagedStructures?: TimberSupplierStructureRef[];
}): Record<string, TimberSupplierStructureRef[]> {
  const options = normalizeQuotedStructures(input.options);
  // Match the staged tags to picker options by id, then by name — hub-CSV node sets
  // can carry generated ids but keep the structure name.
  const staged = normalizeQuotedStructures(
    normalizeQuotedStructures(input.stagedStructures ?? [])
      .map((s) => options.find((o) => o.id === s.id) ?? options.find((o) => normalizeName(o.name) === normalizeName(s.name)))
      .filter((o): o is TimberSupplierStructureRef => !!o),
  );
  const out: Record<string, TimberSupplierStructureRef[]> = {};
  for (const orgId of input.targets) {
    const invite = input.supplierStructuresByOrg?.[orgId];
    if (invite?.length) {
      out[orgId] = normalizeQuotedStructures(invite);
    } else if (staged.length) {
      out[orgId] = [...staged];
    } else if (options.length === 1) {
      out[orgId] = [...options];
    } else {
      out[orgId] = [];
    }
  }
  return out;
}

export function orgIdsMissingPublishStructureSelection(input: {
  targets: string[];
  options: TimberSupplierStructureRef[];
  draftByOrg: Record<string, TimberSupplierStructureRef[]>;
}): string[] {
  if (input.options.length < 2) return [];
  const missing: string[] = [];
  for (const orgId of input.targets) {
    if (!normalizeQuotedStructures(input.draftByOrg[orgId]).length) missing.push(orgId);
  }
  return missing;
}
